import React, { useState, useEffect, useRef } from 'react';
import { X, HelpCircle, MessageCircle, Ticket, Send, ChevronDown, ThumbsUp, Search } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import supportService from '../../services/supportService';

const FloatingHelpWidget = () => {
  const { user, userProfile, getOrganizationId } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('faq'); // faq, chat, ticket
  const [faqs, setFaqs] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [loading, setLoading] = useState(false);

  // Chat state
  const [chatSession, setChatSession] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Ticket state
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    description: '',
    category: 'technical',
    priority: 'medium'
  });
  const [ticketSubmitted, setTicketSubmitted] = useState(false);

  const categories = [
    { value: 'all', label: 'All Topics' },
    { value: 'getting_started', label: 'Getting Started' },
    { value: 'billing', label: 'Billing' },
    { value: 'features', label: 'Features' },
    { value: 'technical', label: 'Technical' },
    { value: 'account', label: 'Account' }
  ];

  // Load FAQs
  useEffect(() => {
    if (isOpen && activeTab === 'faq') {
      loadFAQs();
    }
  }, [isOpen, activeTab, selectedCategory]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Subscribe to chat messages
  useEffect(() => {
    if (chatSession?.id) {
      const unsubscribe = supportService?.subscribeToChatMessages(
        chatSession?.id,
        (newMessage) => {
          setChatMessages((prev) => [...prev, newMessage]);
        }
      );
      return unsubscribe;
    }
  }, [chatSession?.id]);

  const loadFAQs = async () => {
    setLoading(true);
    const category = selectedCategory === 'all' ? null : selectedCategory;
    const { data, error } = await supportService?.getFAQs(category);
    if (!error && data) {
      setFaqs(data);
    }
    setLoading(false);
  };

  const handleFAQClick = (faqId) => {
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
    if (expandedFAQ !== faqId) {
      supportService?.incrementFAQView(faqId);
    }
  };

  const handleFAQHelpful = async (faqId) => {
    await supportService?.markFAQHelpful(faqId);
    loadFAQs();
  };

  const startChat = async () => {
    if (!user || !userProfile) return;
    
    setChatLoading(true);
    const { data, error } = await supportService?.startChatSession(
      user?.id,
      getOrganizationId()
    );
    
    if (!error && data) {
      setChatSession(data);
      const { data: messages } = await supportService?.getChatMessages(data?.id);
      setChatMessages(messages || []);
    }
    setChatLoading(false);
  };

  const sendChatMessage = async () => {
    if (!chatInput?.trim() || !chatSession?.id || !user?.id) return;

    const message = chatInput;
    setChatInput('');
    setChatLoading(true);

    // Send user message
    await supportService?.sendChatMessage(
      chatSession?.id,
      user?.id,
      message,
      false
    );

    // Generate AI response
    try {
      const { data: aiResponse, error } = await supportService?.generateAIResponse(
        message,
        chatMessages
      );

      if (!error && aiResponse) {
        // Send AI response as system message
        await supportService?.sendChatMessage(
          chatSession?.id,
          'ai',
          aiResponse,
          false
        );
      } else {
        // Fallback message if AI fails
        await supportService?.sendChatMessage(
          chatSession?.id,
          'ai',
          'I apologize, but I\'m having trouble generating a response right now. A human support agent will assist you shortly.',
          true
        );
      }
    } catch (error) {
      console.error('Error generating AI response:', error);
      // Send fallback message
      await supportService?.sendChatMessage(
        chatSession?.id,
        'ai',
        'I apologize for the inconvenience. Please try again or submit a support ticket for assistance.',
        true
      );
    }

    setChatLoading(false);
  };

  const endChat = async () => {
    if (chatSession?.id) {
      await supportService?.endChatSession(chatSession?.id);
      setChatSession(null);
      setChatMessages([]);
    }
  };

  const submitTicket = async () => {
    if (!user || !userProfile) return;
    if (!ticketForm?.subject?.trim() || !ticketForm?.description?.trim()) return;

    setLoading(true);
    const { data, error } = await supportService?.createTicket({
      userId: user?.id,
      organizationId: getOrganizationId(),
      subject: ticketForm?.subject,
      description: ticketForm?.description,
      category: ticketForm?.category,
      priority: ticketForm?.priority
    });

    if (!error && data) {
      setTicketSubmitted(true);
      setTimeout(() => {
        setTicketSubmitted(false);
        setTicketForm({
          subject: '',
          description: '',
          category: 'technical',
          priority: 'medium'
        });
      }, 3000);
    }
    setLoading(false);
  };

  const filteredFAQs = faqs?.filter((faq) =>
    searchQuery
      ? faq?.question?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        faq?.answer?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      : true
  );

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-gray-900 hover:bg-gray-800 text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 active:scale-95"
        aria-label="Open help widget"
      >
        <HelpCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col max-h-[600px] animate-in fade-in zoom-in duration-300">
      {/* Header */}
      <div className="bg-gray-900 text-white p-4 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5" />
          <h3 className="font-semibold">Beta Support</h3>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="hover:bg-gray-800 rounded-lg p-1 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('faq')}
          className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
            activeTab === 'faq' ?'text-gray-900 border-b-2 border-gray-900 bg-gray-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <HelpCircle className="w-4 h-4 inline mr-1" />
          FAQ
        </button>
        <button
          onClick={() => {
            setActiveTab('chat');
            if (!chatSession) startChat();
          }}
          className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
            activeTab === 'chat' ?'text-gray-900 border-b-2 border-gray-900 bg-gray-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <MessageCircle className="w-4 h-4 inline mr-1" />
          Live Chat
        </button>
        <button
          onClick={() => setActiveTab('ticket')}
          className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
            activeTab === 'ticket' ?'text-gray-900 border-b-2 border-gray-900 bg-gray-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <Ticket className="w-4 h-4 inline mr-1" />
          Ticket
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {categories?.map((cat) => (
                <option key={cat?.value} value={cat?.value}>
                  {cat?.label}
                </option>
              ))}
            </select>

            {/* FAQ List */}
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading FAQs...</div>
            ) : filteredFAQs?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No FAQs found</div>
            ) : (
              <div className="space-y-2">
                {filteredFAQs?.map((faq) => (
                  <div key={faq?.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => handleFAQClick(faq?.id)}
                      className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-sm text-gray-900">{faq?.question}</span>
                      <ChevronDown
                        className={`w-4 h-4 text-gray-500 transition-transform ${
                          expandedFAQ === faq?.id ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedFAQ === faq?.id && (
                      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                        <p className="text-sm text-gray-700 mb-3">{faq?.answer}</p>
                        <button
                          onClick={() => handleFAQHelpful(faq?.id)}
                          className="text-xs text-gray-900 hover:text-gray-700 flex items-center gap-1"
                        >
                          <ThumbsUp className="w-3 h-3" />
                          Helpful ({faq?.helpful_count || 0})
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="flex flex-col h-full">
            {chatLoading ? (
              <div className="text-center py-8 text-gray-500">Starting chat session...</div>
            ) : !chatSession ? (
              <div className="text-center py-8">
                <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">Start a live chat with our support team</p>
                <Button onClick={startChat}>Start Chat</Button>
              </div>
            ) : (
              <>
                {/* Messages */}
                <div className="flex-1 space-y-3 mb-4 max-h-[300px] overflow-y-auto">
                  {chatMessages?.map((msg) => (
                    <div
                      key={msg?.id}
                      className={`flex ${
                        msg?.sender_id === user?.id ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-2 ${
                          msg?.is_system_message
                            ? 'bg-yellow-50 text-yellow-800 text-xs italic'
                            : msg?.sender_id === user?.id
                            ? 'bg-blue-600 text-white'
                            : msg?.sender_id === 'ai' ?'bg-gray-900 text-white' :'bg-gray-100 text-gray-900'
                        }`}
                      >
                        {msg?.sender_id === 'ai' && (
                          <div className="text-xs opacity-75 mb-1">AI Support Assistant</div>
                        )}
                        <p className="text-sm">{msg?.message}</p>
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-lg px-3 py-2">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e?.target?.value)}
                    onKeyPress={(e) => e?.key === 'Enter' && !chatLoading && sendChatMessage()}
                    placeholder="Type your message..."
                    disabled={chatLoading}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  />
                  <Button onClick={sendChatMessage} size="sm" disabled={chatLoading || !chatInput?.trim()}>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>

                <button
                  onClick={endChat}
                  className="mt-2 text-xs text-red-600 hover:text-red-700 text-center w-full"
                >
                  End Chat
                </button>
              </>
            )}
          </div>
        )}

        {/* Ticket Tab */}
        {activeTab === 'ticket' && (
          <div className="space-y-4">
            {ticketSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Ticket className="w-8 h-8 text-gray-700" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Ticket Submitted!</h4>
                <p className="text-sm text-gray-600">We will get back to you within 24 hours.</p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={ticketForm?.subject}
                    onChange={(e) => setTicketForm({ ...ticketForm, subject: e?.target?.value })}
                    placeholder="Brief description of your issue"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={ticketForm?.category}
                    onChange={(e) => setTicketForm({ ...ticketForm, category: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="getting_started">Getting Started</option>
                    <option value="billing">Billing</option>
                    <option value="features">Features</option>
                    <option value="technical">Technical</option>
                    <option value="account">Account</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={ticketForm?.priority}
                    onChange={(e) => setTicketForm({ ...ticketForm, priority: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={ticketForm?.description}
                    onChange={(e) => setTicketForm({ ...ticketForm, description: e?.target?.value })}
                    placeholder="Provide detailed information about your issue"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  />
                </div>

                <Button
                  onClick={submitTicket}
                  disabled={loading || !ticketForm?.subject?.trim() || !ticketForm?.description?.trim()}
                  fullWidth
                >
                  {loading ? 'Submitting...' : 'Submit Ticket'}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingHelpWidget;